import {
  countUniqueDaysWithin,
  dayDiffFromNow,
  parseLocalDateValue,
  uniqueDateValues,
} from "../features/behavior/utils/canonicalSummary";

function uniqueSortedDates(values) {
  return uniqueDateValues(values).sort(
    (a, b) =>
      (parseLocalDateValue(b)?.getTime() || 0) - (parseLocalDateValue(a)?.getTime() || 0)
  );
}

function countWithinDays(values, days) {
  return countUniqueDaysWithin(values, days);
}

export function formatRelativeDateLabel(value) {
  if (!value) return "";
  const diff = dayDiffFromNow(value);
  if (diff === null) return "";
  if (diff === 0) return "today";
  if (diff === 1) return "yesterday";
  if (diff < 7) return `${diff} days ago`;
  const date = parseLocalDateValue(value);
  return date
    ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";
}

export function buildHeroMomentumSignal({
  state,
  checkinDates,
  actionCompletionDates,
  checkinStreak,
  hasCheckinToday,
  hasCompletedAnyActionToday,
}) {
  const actionCount7 = countWithinDays(actionCompletionDates, 7);
  const visibleDays4 = countWithinDays(
    [...new Set([...(checkinDates || []), ...(actionCompletionDates || [])])],
    4
  );

  if (state === "reentry" && (hasCheckinToday || hasCompletedAnyActionToday)) {
    return { text: "Back today after a miss.", tone: "positive" };
  }
  if (hasCompletedAnyActionToday && actionCount7 > 1) {
    return {
      text: `${actionCount7} habits this week.`,
      tone: "positive",
    };
  }
  if (hasCheckinToday && checkinStreak > 1) {
    return { text: `${checkinStreak} active days in a row.`, tone: "positive" };
  }
  if (visibleDays4 >= 3) {
    return {
      text: `${visibleDays4} of the last 4 days active.`,
      tone: state === "drift" ? "warning" : "neutral",
    };
  }
  if (state === "drift") {
    return { text: "Nothing from you yet today.", tone: "warning" };
  }
  if (state === "dormant") {
    return { text: "A small step is enough to restart this.", tone: "neutral" };
  }
  return null;
}

export function buildActionSignal({ completionDates, completedToday }) {
  const dates = uniqueSortedDates(completionDates || []);
  const recentCount7 = countWithinDays(dates, 7);
  const lastDone = dates[0];
  const historyItems = dates
    .slice(0, 3)
    .map((value) => `Completed ${formatRelativeDateLabel(value)}`);

  if (completedToday) {
    return {
      summary: recentCount7 > 1 ? `Done ${recentCount7} times this week` : "Done today",
      historySummary: historyItems.length ? "Recent activity" : "This is live again",
      historyItems,
    };
  }

  if (lastDone) {
    return {
      summary:
        recentCount7 > 1
          ? `Done ${recentCount7} times this week`
          : `Last done ${formatRelativeDateLabel(lastDone)}`,
      historySummary: "Recent activity",
      historyItems,
    };
  }

  return {
    summary: "No visible activity yet",
    historySummary: "Start the first visible step",
    historyItems: [],
  };
}

export function buildRecentSignalItems({
  checkinStreak,
  checkinDates,
  actionCompletionDates,
  partnerUsername,
  partnerEngagedToday,
  state,
}) {
  const items = [];
  const actionCount7 = countWithinDays(actionCompletionDates || [], 7);
  const visibleDays4 = countWithinDays(
    [...new Set([...(checkinDates || []), ...(actionCompletionDates || [])])],
    4
  );

  if (checkinStreak > 1) {
    items.push({
      label: "Momentum",
      value: `${checkinStreak} active days recently`,
      tone: "positive",
    });
  } else if (visibleDays4 > 0) {
    items.push({
      label: "Recently",
      value: `${visibleDays4} of last 4 days active`,
      tone: state === "drift" ? "warning" : "neutral",
    });
  }

  if (actionCount7 > 0) {
    items.push({
      label: "This week",
      value: `${actionCount7} habit${actionCount7 === 1 ? "" : "s"} this week`,
      tone: "neutral",
    });
  }

  if (partnerUsername && partnerEngagedToday) {
    items.push({
      label: "Accountability",
      value: `@${partnerUsername} showed up today`,
      tone: "positive",
    });
  } else if (partnerUsername) {
    items.push({
      label: "Accountability",
      value: `@${partnerUsername} is your active partner`,
      tone: "neutral",
    });
  }

  return items.slice(0, 3);
}

export function buildSprintSignalSummary({
  sharedStreak,
  lastActivityAt,
  state,
  partnerUsername,
}) {
  if (state === "dormant") {
    return { text: "This Accountability has gone quiet. One step brings it back.", tone: "warning" };
  }
  if (sharedStreak > 1) {
    return {
      text: `You both showed up ${sharedStreak} days in a row.`,
      tone: "positive",
    };
  }
  const relative = formatRelativeDateLabel(lastActivityAt);
  if (relative) {
    return {
      text:
        relative === "today"
          ? "Someone showed up in this Accountability today."
          : `Last activity here was ${relative}.`,
      tone: relative === "today" || relative === "yesterday" ? "positive" : "neutral",
    };
  }
  if (partnerUsername) {
    return {
      text: `@${partnerUsername} is still in Accountability with you.`,
      tone: "neutral",
    };
  }
  return null;
}

export function buildSprintRecentItems({
  sharedStreak,
  lastActivityAt,
  lastActivityLabel,
  recoveryLabel,
  notesThisWeek,
  partnerUsername,
  partnerEngagedToday,
}) {
  const items = [];

  if (sharedStreak > 0) {
    items.push({
      label: "Together",
      value:
        sharedStreak > 1
          ? `${sharedStreak} days showing up together`
          : "You both showed up once",
      tone: sharedStreak > 1 ? "positive" : "neutral",
    });
  }

  if (typeof notesThisWeek === "number") {
    items.push({
      label: "This week",
      value: `${notesThisWeek} shared update${notesThisWeek === 1 ? "" : "s"}`,
      tone: notesThisWeek > 0 ? "positive" : "neutral",
    });
  }

  if (partnerUsername && partnerEngagedToday) {
    items.push({
      label: "Partner",
      value: `@${partnerUsername} showed up today`,
      tone: "positive",
    });
  } else if (lastActivityLabel || lastActivityAt) {
    items.push({
      label: "Last activity",
      value: lastActivityLabel || formatRelativeDateLabel(lastActivityAt),
      tone: "neutral",
    });
  }

  if (recoveryLabel) {
    items.push({
      label: "State",
      value: recoveryLabel,
      tone: "warning",
    });
  }

  return items.slice(0, 4);
}
